import React, { useEffect, useMemo } from 'react';
import { Button, Card, DatePicker, Input, InputNumber, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';

import ConditionBuilder from '../common/ConditionBuilder';
import { InputSelect } from '../Input';
import { AGGREGATE_OPTIONS, WINDOW_UNIT_OPTIONS } from '../cards/DrivenFromModal';
import { buildCardHierarchyOptions } from '../cards/CardVisuals';
import { CLOSE } from '../icons';
import { booleanLabels } from '../../utils/elements';
import { defaultUnitFor, unitSymbol } from '../../utils/measurements';
import { normalizeId } from '../../utils/ids';
import {
	DATE_OPS,
	RULE_LIMITS,
	WINDOW_SYSTEM_FIELDS,
	clauseSource,
	clauseValueKind,
	defaultValueFor,
	describeClause,
	opFitsKind,
	opsForKind,
	sourceElementsFor,
	whenNoneApplies,
	windowElementsOf,
} from '../../utils/tagRules';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

const { Text } = Typography;

/**
 * ONE RELATED CLAUSE of an automatic tag's rule (docs/plans/tags.md §3.4:
 * `{ card_id, link, filter, window, aggregate, of, op, value, whenNone }`) —
 * "the latest Oil change is older than 180 days", "fewer than 1 Visit in the
 * last 30 days", "Orders in the last 90 days total more than 2,000".
 *
 * It asks what a driven element asks (components/cards/DrivenFromModal —
 * its aggregate words and window units are the same constants), then how to
 * compare the answer:
 *
 *   Card              which card's items point at this one (only cards with
 *                     an item lookup to the rule's card, or a card above it)
 *   Linked by         the lookup — chosen for you when there is only one
 *   What to take      latest · count · sum · smallest · largest · average
 *   Of                the element (a count has none); numeric aggregates
 *                     offer number, measurement and money elements only
 *   Is                the comparisons that value takes (a date: older than,
 *                     within the last / next, before, after…)
 *   Value             a number (a measurement in its element's unit, money
 *                     in its element's currency), a date, a span, yes / no,
 *                     or text
 *   Only the last …   an optional time window, measured by a date of theirs
 *   And only those    an optional filter on the related items
 *   When there are none  what a blank answer counts as (not for a count or a
 *                     sum, which compare their zero)
 *
 * `error` is `{ key, text }` — the API's refusal of this clause, said under
 * the control it names.
 */
export default function TagRuleClause({
	clause,
	index,
	relatedCards = [],
	cardsById = {},
	elementsFor,
	collectionId,
	onChange,
	onRemove,
	error = null,
	phone = false,
}) {
	const n = index + 1;
	const entry = relatedCards.find(candidate => normalizeId(candidate.card?._id) === clause.card_id) || null;
	const card = entry?.card || cardsById[clause.card_id] || null;
	const elements = useMemo(() => (clause.card_id && typeof elementsFor === 'function' ? elementsFor(clause.card_id) || [] : []), [clause.card_id, elementsFor]);
	const links = useMemo(() => entry?.links || [], [entry]);
	const cardOptions = useMemo(() => buildCardHierarchyOptions(relatedCards.map(candidate => candidate.card)), [relatedCards]);

	// Exactly one lookup is the common case (an Oil change has one Vehicle):
	// it is chosen for the person, as the driven element's editor does.
	useEffect(() => {
		if (!clause.card_id) return;
		const names = links.map(link => link.name);
		if (clause.link && names.includes(clause.link)) return;
		const next = names.length === 1 ? names[0] : '';
		if (next !== clause.link) onChange({ link: next });
	}, [clause.card_id, clause.link, links, onChange]);

	const kind = clauseValueKind(clause, elements);
	const source = clauseSource(clause, elements);
	const ops = opsForKind(kind);
	const sources = sourceElementsFor(elements, clause.aggregate);

	// A changed "of" or aggregate can leave a comparison the new value does
	// not take: it is replaced, never saved stale.
	useEffect(() => {
		if (!kind || !clause.op) return;
		if (!opFitsKind(clause.op, kind)) onChange({ op: '', value: null });
	}, [kind, clause.op, onChange]);

	const fieldError = key => (error && error.key === key ? error.text : null);
	const errorFor = keys => keys.map(fieldError).find(Boolean) || null;

	const setAggregate = aggregate => {
		const keepOf = aggregate !== 'count' && sourceElementsFor(elements, aggregate).some(el => el.name === clause.of);
		onChange({ aggregate, of: keepOf ? clause.of : '', ...(keepOf ? {} : { op: '', value: null }) });
	};

	const setOp = op => {
		const wasSpan = DATE_OPS.has(clause.op);
		const isSpan = DATE_OPS.has(op);
		onChange({ op, ...(wasSpan !== isSpan || clause.value === null ? { value: defaultValueFor(kind, op) } : {}) });
	};

	const valueInput = () => {
		if (!clause.op) return null;
		if (DATE_OPS.has(clause.op)) {
			return (
				<Space.Compact className="tag-rule-clause__span">
					<InputNumber
						min={1}
						max={RULE_LIMITS.every}
						precision={0}
						value={clause.value?.every ?? null}
						aria-label={`Related items ${n}: how many`}
						onChange={every => onChange({ value: { ...(clause.value || { unit: 'day' }), every } })}
					/>
					<Select
						value={clause.value?.unit || 'day'}
						options={WINDOW_UNIT_OPTIONS}
						aria-label={`Related items ${n}: unit`}
						onChange={unit => onChange({ value: { ...(clause.value || { every: 30 }), unit } })}
					/>
				</Space.Compact>
			);
		}
		if (kind === 'date') {
			return (
				<DatePicker
					value={clause.value ? dayjs(clause.value) : null}
					format="YYYY-MM-DD"
					aria-label={`Related items ${n}: date`}
					onChange={date => onChange({ value: date ? date.format('YYYY-MM-DD') : '' })}
				/>
			);
		}
		if (kind === 'boolean') {
			const { trueLabel, falseLabel } = booleanLabels(source);
			return (
				<Select
					className="tag-rule-clause__value"
					value={typeof clause.value === 'boolean' ? clause.value : undefined}
					aria-label={`Related items ${n}: yes or no`}
					options={[{ value: true, label: trueLabel }, { value: false, label: falseLabel }]}
					onChange={value => onChange({ value })}
				/>
			);
		}
		if (kind === 'text') {
			return (
				<Input
					{...NO_TEXT_ASSIST}
					className="tag-rule-clause__value"
					maxLength={RULE_LIMITS.textValue}
					value={typeof clause.value === 'string' ? clause.value : ''}
					aria-label={`Related items ${n}: text`}
					onChange={event => onChange({ value: event.target.value })}
				/>
			);
		}
		// A measurement is read in its element's own unit and money in its
		// element's currency (the API compares a bare number that way).
		const unitHint = kind === 'measurement'
			? (unitSymbol(defaultUnitFor(source?.options || {})) || null)
			: (kind === 'currency' ? (source?.options?.defaultCurrency || null) : null);
		return (
			<span className="tag-rule-clause__number">
				<InputNumber
					className="tag-rule-clause__value"
					value={typeof clause.value === 'number' ? clause.value : null}
					aria-label={`Related items ${n}: number`}
					onChange={value => onChange({ value: typeof value === 'number' ? value : null })}
				/>
				{unitHint ? <span className="tag-rule-clause__unit">{`in ${unitHint}`}</span> : null}
			</span>
		);
	};

	const summary = describeClause(clause, { card, elements });
	const windowFieldOptions = [
		...windowElementsOf(elements).map(el => ({ value: el.name, label: el.label?.singular || el.name })),
		...WINDOW_SYSTEM_FIELDS,
	];

	return (
		<Card
			size="small"
			className="tag-rule-clause"
			data-testid="tag-rule-clause"
			title={<span className="tag-rule-clause__title">{`Related items ${n}`}</span>}
			extra={(
				<Button
					type="text"
					icon={<CLOSE />}
					aria-label={`Remove related items ${n}`}
					onClick={onRemove}
				/>
			)}
		>
			{summary ? <p className="tag-rule-clause__summary" data-testid="tag-rule-clause-summary">{summary}</p> : null}

			<div className="tag-rule-clause__field">
				<InputSelect
					elementName={`tagRuleClauseCard-${clause.key}`}
					elementLabel="Card"
					value={clause.card_id || undefined}
					onChange={(name, value) => onChange({ card_id: value || '', link: '', of: '', op: '', value: null, filter: '', window: null })}
					options={cardOptions}
					placeholder="Whose items point at this one?"
					allowClear={false}
					status={errorFor(['card_id']) ? 'error' : undefined}
				/>
				{errorFor(['card_id']) ? <Text type="danger" className="tag-rule__error">{errorFor(['card_id'])}</Text> : null}
			</div>

			{clause.card_id && links.length > 1 ? (
				<div className="tag-rule-clause__field">
					<label htmlFor={`tagRuleClauseLink-${clause.key}`} className="d-block">Linked by</label>
					<Select
						id={`tagRuleClauseLink-${clause.key}`}
						className="tag-rule-clause__select"
						value={clause.link || undefined}
						placeholder="Which item lookup points at this card?"
						options={links.map(link => ({ value: link.name, label: link.label?.singular || link.name }))}
						status={errorFor(['link']) ? 'error' : undefined}
						onChange={link => onChange({ link })}
					/>
				</div>
			) : null}
			{clause.card_id && links.length === 1 ? (
				<Text type="secondary" className="d-block tag-rule-clause__note">
					Linked by <strong>{links[0].label?.singular || links[0].name}</strong>.
				</Text>
			) : null}
			{errorFor(['link']) ? <Text type="danger" className="tag-rule__error">{errorFor(['link'])}</Text> : null}

			{clause.card_id ? (
				<div className={['tag-rule-clause__row', phone ? 'tag-rule-clause__row--stack' : ''].filter(Boolean).join(' ')}>
					<div className="tag-rule-clause__field">
						<label htmlFor={`tagRuleClauseAggregate-${clause.key}`} className="d-block">What to take</label>
						<Select
							id={`tagRuleClauseAggregate-${clause.key}`}
							className="tag-rule-clause__select"
							value={clause.aggregate}
							options={AGGREGATE_OPTIONS}
							onChange={setAggregate}
						/>
					</div>
					{clause.aggregate !== 'count' ? (
						<div className="tag-rule-clause__field">
							<label htmlFor={`tagRuleClauseOf-${clause.key}`} className="d-block">Of</label>
							<Select
								id={`tagRuleClauseOf-${clause.key}`}
								className="tag-rule-clause__select"
								value={clause.of || undefined}
								placeholder="Which element?"
								options={sources.map(el => ({ value: el.name, label: el.label?.singular || el.label || el.name }))}
								status={errorFor(['of']) ? 'error' : undefined}
								notFoundContent={`${card?.name || 'This card'} has no element that can be added up`}
								onChange={of => onChange({ of })}
							/>
						</div>
					) : null}
				</div>
			) : null}
			{errorFor(['aggregate', 'of']) ? <Text type="danger" className="tag-rule__error">{errorFor(['aggregate', 'of'])}</Text> : null}

			{kind ? (
				<div className={['tag-rule-clause__row', phone ? 'tag-rule-clause__row--stack' : ''].filter(Boolean).join(' ')}>
					<div className="tag-rule-clause__field">
						<label htmlFor={`tagRuleClauseOp-${clause.key}`} className="d-block">Is</label>
						<Select
							id={`tagRuleClauseOp-${clause.key}`}
							className="tag-rule-clause__select"
							value={clause.op || undefined}
							placeholder="How to compare it"
							options={ops}
							status={errorFor(['op']) ? 'error' : undefined}
							onChange={setOp}
						/>
					</div>
					{clause.op ? (
						<div className="tag-rule-clause__field">
							<span className="d-block tag-rule-clause__label">Value</span>
							{valueInput()}
						</div>
					) : null}
				</div>
			) : null}
			{errorFor(['op', 'value']) ? <Text type="danger" className="tag-rule__error">{errorFor(['op', 'value'])}</Text> : null}

			{clause.card_id ? (
				<div className="tag-rule-clause__narrow">
					<Text strong className="d-block">Which of them to count</Text>
					<div className={['tag-rule-clause__row', phone ? 'tag-rule-clause__row--stack' : ''].filter(Boolean).join(' ')}>
						<div className="tag-rule-clause__field">
							<label htmlFor={`tagRuleClauseWindow-${clause.key}`} className="d-block">Only the last</label>
							<Space.Compact>
								<InputNumber
									min={1}
									max={RULE_LIMITS.every}
									precision={0}
									value={clause.window?.every ?? 30}
									disabled={!clause.window?.field}
									aria-label={`Related items ${n}: window length`}
									onChange={every => onChange({ window: { ...(clause.window || { unit: 'day' }), every: every || 1 } })}
								/>
								<Select
									value={clause.window?.unit || 'day'}
									disabled={!clause.window?.field}
									aria-label={`Related items ${n}: window unit`}
									options={WINDOW_UNIT_OPTIONS}
									onChange={unit => onChange({ window: { ...(clause.window || { every: 30 }), unit } })}
								/>
							</Space.Compact>
						</div>
						<div className="tag-rule-clause__field">
							<label htmlFor={`tagRuleClauseWindow-${clause.key}`} className="d-block">measured by</label>
							<Select
								id={`tagRuleClauseWindow-${clause.key}`}
								className="tag-rule-clause__select"
								allowClear
								value={clause.window?.field || undefined}
								placeholder="No time window"
								options={windowFieldOptions}
								status={errorFor(['window']) ? 'error' : undefined}
								onChange={field => onChange({ window: field ? { every: clause.window?.every || 30, unit: clause.window?.unit || 'day', field } : null })}
							/>
						</div>
					</div>
					{errorFor(['window']) ? <Text type="danger" className="tag-rule__error">{errorFor(['window'])}</Text> : null}
					<div className="tag-rule-clause__field">
						<Text className="d-block">And only those matching</Text>
						<ConditionBuilder
							value={clause.filter || ''}
							onChange={filter => onChange({ filter })}
							elements={elements}
							collectionId={collectionId}
						/>
						{errorFor(['filter']) ? <Text type="danger" className="tag-rule__error">{errorFor(['filter'])}</Text> : null}
					</div>
				</div>
			) : null}

			{clause.card_id && whenNoneApplies(clause) ? (
				<div className="tag-rule-clause__field">
					<label htmlFor={`tagRuleClauseNone-${clause.key}`} className="d-block">When there are none</label>
					<Select
						id={`tagRuleClauseNone-${clause.key}`}
						className="tag-rule-clause__select"
						value={clause.whenNone === true}
						options={[
							{ value: false, label: 'This part does not hold' },
							{ value: true, label: 'This part holds' },
						]}
						onChange={whenNone => onChange({ whenNone })}
					/>
					<Text type="secondary" className="d-block tag-rule-clause__note">
						{`When no ${card?.label?.singular || card?.name || 'related item'} has a value yet.`}
					</Text>
					{errorFor(['whenNone']) ? <Text type="danger" className="tag-rule__error">{errorFor(['whenNone'])}</Text> : null}
				</div>
			) : null}
		</Card>
	);
}
